import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import {
  DEFAULT_DURATION_MIN,
  DRAG_THRESHOLD_PX,
  MOBILE_DAY_COUNT,
  eventDisplayTitle,
  labelForCategory,
  startOfWeekMonday,
  buildWeekDays,
  addDays,
  formatWeekRangeLabel,
  formatMobileRangeLabel,
  toIsoDateLocal,
  parseIsoDateLocal,
} from './constants';
import {
  minutesToTop,
  yToMinutes,
  yToEndMinutes,
  rangeFromDrag,
  clampMoveStart,
  clampResizeEnd,
  minutesToTimeValue,
  timeValueToMinutes,
  formatMinutes,
  eventHeight,
} from './timeUtils';
import { computeAllLayouts } from './layoutUtils';
import { loadEventGroups, saveEventGroups } from './groupsStorage';
import {
  apiEventToWeekEvent,
  weekEventToApiPayload,
  expandEventToDaySegments,
  isEndAfterStart,
  addDaysToIso,
} from '../planning/eventApiMappers';
import {
  formatApiError,
  getEvents,
  createEvent,
  updateEvent,
  deleteEvent,
} from '../../services/api';
import WeekDayHeaders from './WeekDayHeaders';
import TimeGutter from './TimeGutter';
import DayColumn from './DayColumn';
import CreateEventPopover from './CreateEventPopover';
import EditEventPopover from './EditEventPopover';
import DeleteConfirmModal from './DeleteConfirmModal';
import ImportOutlookModal from './ImportOutlookModal';
import Toast from '../Toast';
import useToast from '../../hooks/useToast';
import { SelectionBar, PasteGroupBanner, GroupsDropdown } from './SelectionBar';
import './weeklyView.css';
import { useLocation, useNavigate } from 'react-router-dom';

let idCounter = 0;
function nextId() {
  idCounter += 1;
  return `ev-${Date.now()}-${idCounter}`;
}

function isPersistedId(id) {
  return typeof id === 'number' || (typeof id === 'string' && /^\d+$/.test(id));
}

const ALL_DAY_START = '00:00';
const ALL_DAY_END = '23:59';
const DEFAULT_TIMED_START = '09:00';
const DEFAULT_TIMED_END = '10:00';

function isAllDayTimes(startTime, endTime) {
  const start = timeValueToMinutes(startTime);
  const end = timeValueToMinutes(endTime);
  return start === 0 && end >= 24 * 60;
}

function isAllDayMinutes(start, end) {
  return Number(start) === 0 && Number(end) >= 24 * 60;
}

function applyAllDayToggle(state, checked) {
  if (!state) return state;
  if (checked) {
    return {
      ...state,
      allDay: true,
      prevStartTime: state.startTime,
      prevEndTime: state.endTime,
      startTime: ALL_DAY_START,
      endTime: ALL_DAY_END,
      start: 0,
      end: 24 * 60,
      timeError: false,
    };
  }
  const startTime = state.prevStartTime || DEFAULT_TIMED_START;
  const endTime = state.prevEndTime || DEFAULT_TIMED_END;
  return {
    ...state,
    allDay: false,
    startTime,
    endTime,
    start: timeValueToMinutes(startTime),
    end: timeValueToMinutes(endTime),
    timeError: false,
  };
}

function positionNearRect(anchorRect, tipWidth = 288, tipHeight = 320) {
  const gap = 12;
  let left = anchorRect.right + gap;
  if (left + tipWidth > window.innerWidth - 16) {
    left = anchorRect.left - tipWidth - gap;
  }
  if (left < 16) left = 16;
  let top = anchorRect.top + anchorRect.height / 2 - tipHeight / 2;
  if (top + tipHeight > window.innerHeight - 16) {
    top = window.innerHeight - tipHeight - 16;
  }
  if (top < 16) top = 16;
  return { left, top };
}

function initialMobileDayStart(weekDays) {
  const todayIdx = weekDays.findIndex((d) => d.today);
  if (todayIdx < 0) return 0;
  return Math.max(0, Math.min(7 - MOBILE_DAY_COUNT, todayIdx - 1));
}

const weekSlideVariants = {
  enter: (direction) => ({
    x: direction > 0 ? 56 : -56,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction) => ({
    x: direction > 0 ? -56 : 56,
    opacity: 0,
  }),
};

function getApiErrorMessage(err, fallback) {
  const formatted = formatApiError(err);
  return formatted.generalError || err?.message || fallback;
}

const EmployeeWeeklyViewPage = forwardRef(function EmployeeWeeklyViewPage(
  { onWeekRangeChange, weekStartIso = null, onWeekStartChange, userId = null, readOnly = false },
  ref
) {
  const { t } = useTranslation('planning');
  const location = useLocation();
  const navigate = useNavigate();
  const [importOutlookOpen, setImportOutlookOpen] = useState(false);
  const resolveWeekStart = useCallback((iso) => {
    if (iso) {
      const parsed = parseIsoDateLocal(iso);
      if (parsed) return startOfWeekMonday(parsed);
    }
    return startOfWeekMonday();
  }, []);

  const [weekStart, setWeekStart] = useState(() => resolveWeekStart(weekStartIso));
  const [weekDirection, setWeekDirection] = useState(0);
  const weekDays = useMemo(() => buildWeekDays(weekStart), [weekStart]);
  const weekDaysRef = useRef(weekDays);
  weekDaysRef.current = weekDays;
  const weekKey = weekStart.getTime();
  const onWeekStartChangeRef = useRef(onWeekStartChange);
  onWeekStartChangeRef.current = onWeekStartChange;

  // Follow URL / prop when the parent opens a different week
  useEffect(() => {
    if (!weekStartIso) return;
    const next = resolveWeekStart(weekStartIso);
    setWeekStart((prev) => (prev.getTime() === next.getTime() ? prev : next));
  }, [weekStartIso, resolveWeekStart]);

  // Keep shareable URL in sync with the visible week
  useEffect(() => {
    onWeekStartChangeRef.current?.(toIsoDateLocal(weekStart));
  }, [weekStart]);

  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [groups, setGroups] = useState(() => loadEventGroups());
  const [groupName, setGroupName] = useState('');
  const [pasteGroup, setPasteGroup] = useState(null);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 768px)').matches);
  const [mobileDayStart, setMobileDayStart] = useState(() =>
    initialMobileDayStart(buildWeekDays(startOfWeekMonday()))
  );
  const [nowMinutes, setNowMinutes] = useState(() => {
    const n = new Date();
    return n.getHours() * 60 + n.getMinutes();
  });
  const [slotHover, setSlotHover] = useState(null);
  const [dragSelect, setDragSelect] = useState(null);
  const [interaction, setInteraction] = useState(null);
  const [createState, setCreateState] = useState(null);
  const [editState, setEditState] = useState(null);
  const [deleteState, setDeleteState] = useState(null);
  const { toast, showError, showSuccess, hideToast } = useToast();

  const columnRefs = useRef({});
  const rootRef = useRef(null);
  const scrollRef = useRef(null);
  const dragCreateRef = useRef(null);
  const moveRef = useRef(null);
  const copyRef = useRef(null);
  const resizeRef = useRef(null);
  const eventsRef = useRef(events);
  const selectedRef = useRef(selectedIds);
  const pasteRef = useRef(pasteGroup);
  const suppressClickRef = useRef(false);
  const suppressDeleteRef = useRef(false);
  const [timePeriod, setTimePeriod] = useState(() => {
    const n = new Date();
    return n.getHours() >= 12 ? 'pm' : 'am';
  });
  const timePeriodRef = useRef(timePeriod);
  timePeriodRef.current = timePeriod;

  useEffect(() => {
    eventsRef.current = events;
  }, [events]);
  useEffect(() => {
    selectedRef.current = selectedIds;
  }, [selectedIds]);
  useEffect(() => {
    pasteRef.current = pasteGroup;
  }, [pasteGroup]);

  const weekRangeLabel = useMemo(() => {
    if (!isMobile) return formatWeekRangeLabel(weekDays);
    return formatMobileRangeLabel(weekDays, mobileDayStart, MOBILE_DAY_COUNT);
  }, [isMobile, mobileDayStart, weekDays]);

  useEffect(() => {
    onWeekRangeChange?.(weekRangeLabel);
  }, [weekRangeLabel, onWeekRangeChange]);

  const showEventError = useCallback(
    (err, fallback) => {
      showError(t('schedulingError'), getApiErrorMessage(err, fallback), 5000);
    },
    [showError]
  );

  const reloadWeekEvents = useCallback(
    async (cancelRef = { current: false }) => {
      const cancelled = Boolean(cancelRef?.current);
      const dateFrom = weekDaysRef.current[0]?.iso;
      const dateTo = weekDaysRef.current[6]?.iso;
      if (!dateFrom || !dateTo || cancelled) return;

      const filters = { date_from: dateFrom, date_to: dateTo };
      if (userId != null) filters.employe = userId;
      const data = await getEvents(filters);
      const list = Array.isArray(data) ? data : data?.results || [];
      if (cancelRef?.current) return;
      setEvents(list.map((ev) => apiEventToWeekEvent(ev, dateFrom)));
    },
    [userId]
  );

  useEffect(() => {
    const cancelRef = { current: false };
    const dateFrom = weekDays[0]?.iso;
    const dateTo = weekDays[6]?.iso;
    if (!dateFrom || !dateTo) return undefined;

    (async () => {
      setEventsLoading(true);
      try {
        await reloadWeekEvents(cancelRef);
      } catch (err) {
        if (cancelRef.current) return;
        console.error('Failed to load events', err);
        showEventError(err, t('failedToLoadEvents'));
        setEvents([]);
      } finally {
        if (!cancelRef.current) setEventsLoading(false);
      }
    })();

    return () => {
      cancelRef.current = true;
    };
  }, [reloadWeekEvents, showEventError, weekKey, weekDays]);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const onChange = () => setIsMobile(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const t = setInterval(() => {
      const n = new Date();
      setNowMinutes(n.getHours() * 60 + n.getMinutes());
    }, 60000);
    return () => clearInterval(t);
  }, []);

  const dayMetaByIso = useCallback((iso) => {
    return weekDaysRef.current.find((d) => d.iso === iso) || weekDaysRef.current[0];
  }, []);

  const dayMetaByIndex = useCallback((day) => {
    return weekDaysRef.current.find((d) => d.day === day) || weekDaysRef.current[0];
  }, []);

  const visibleDays = useMemo(() => {
    if (!isMobile) return new Set(weekDays.map((d) => d.day));
    const set = new Set();
    for (let i = 0; i < MOBILE_DAY_COUNT; i++) set.add(mobileDayStart + i);
    return set;
  }, [isMobile, mobileDayStart, weekDays]);

  const filteredEvents = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return events;
    return events.filter((ev) => {
      const title = String(ev.title || '').toLowerCase();
      const cat = labelForCategory(ev.category).toLowerCase();
      const code = String(ev.category || '').toLowerCase();
      return title.includes(q) || cat.includes(q) || code.includes(q);
    });
  }, [events, searchQuery]);

  const displaySegments = useMemo(() => {
    const weekStartIso = weekDays[0]?.iso;
    if (!weekStartIso) return [];
    return filteredEvents.flatMap((ev) => {
      const segs = expandEventToDaySegments(ev, { weekStartIso, useMinutes: true });
      return segs.filter((s) => Number.isFinite(s.day) && s.day >= 0 && s.day <= 6);
    });
  }, [filteredEvents, weekDays]);

  const layouts = useMemo(() => computeAllLayouts(displaySegments), [displaySegments]);

  const eventsByDay = useMemo(() => {
    const map = {};
    weekDays.forEach((d) => {
      map[d.day] = [];
    });
    displaySegments.forEach((ev) => {
      if (map[ev.day]) map[ev.day].push(ev);
    });
    return map;
  }, [displaySegments, weekDays]);

  const getColumnY = useCallback((day, clientY) => {
    const el = columnRefs.current[day];
    if (!el) return 0;
    return clientY - el.getBoundingClientRect().top;
  }, []);

  const getDayAt = useCallback((clientX, clientY) => {
    const days = weekDaysRef.current;
    for (const d of days) {
      const el = columnRefs.current[d.day];
      if (!el || (isMobile && !visibleDays.has(d.day))) continue;
      const r = el.getBoundingClientRect();
      if (clientX >= r.left && clientX < r.right && clientY >= r.top && clientY < r.bottom) {
        return d.day;
      }
    }
    for (const d of days) {
      const el = columnRefs.current[d.day];
      if (!el || (isMobile && !visibleDays.has(d.day))) continue;
      const r = el.getBoundingClientRect();
      if (clientX >= r.left && clientX < r.right) return d.day;
    }
    return null;
  }, [isMobile, visibleDays]);

  const closeCreate = useCallback(() => {
    setCreateState(null);
    setDragSelect(null);
  }, []);

  const closeEdit = useCallback(() => setEditState(null), []);
  const closeDelete = useCallback(() => setDeleteState(null), []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setGroupName('');
  }, []);

  const exitPaste = useCallback(() => setPasteGroup(null), []);

  const openCreateAt = useCallback((day, start, end, anchorRect, centered = false) => {
    setEditState(null);
    setDeleteState(null);
    setGroupsOpen(false);
    const meta = dayMetaByIndex(day);
    const style = centered
      ? { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }
      : (() => {
          const pos = positionNearRect(anchorRect || { left: 100, top: 100, right: 200, bottom: 200, height: 60 });
          return { left: pos.left, top: pos.top, transform: 'none' };
        })();
    setDragSelect({
      day,
      top: minutesToTop(start),
      height: eventHeight(start, end),
      preview: false,
    });
    setCreateState({
      day,
      dayIso: meta.iso,
      endDayIso: meta.iso,
      start,
      end,
      startTime: minutesToTimeValue(start),
      endTime: minutesToTimeValue(end),
      allDay: false,
      title: '',
      category: 'HO',
      timeError: false,
      style,
      heading: `${meta.label}, ${meta.date} · ${formatMinutes(start)} – ${formatMinutes(end)}`,
    });
  }, [dayMetaByIndex]);

  const openCreateEvent = useCallback(() => {
    if (readOnly) return;
    const days = weekDaysRef.current;
    const today = days.find((d) => d.today) || days[0];
    const start = Math.floor(nowMinutes / 30) * 30;
    const end = Math.min(start + DEFAULT_DURATION_MIN, 24 * 60);
    openCreateAt(today.day, start, end, null, true);
  }, [nowMinutes, openCreateAt, readOnly]);

  const goPrevDays = useCallback(() => {
    if (isMobile) {
      if (mobileDayStart > 0) {
        setWeekDirection(-1);
        setMobileDayStart((s) => s - 1);
      } else {
        setWeekDirection(-1);
        setWeekStart((ws) => addDays(ws, -7));
        setMobileDayStart(7 - MOBILE_DAY_COUNT);
      }
      return;
    }
    setWeekDirection(-1);
    setWeekStart((ws) => addDays(ws, -7));
  }, [isMobile, mobileDayStart]);

  const goNextDays = useCallback(() => {
    if (isMobile) {
      if (mobileDayStart < 7 - MOBILE_DAY_COUNT) {
        setWeekDirection(1);
        setMobileDayStart((s) => s + 1);
      } else {
        setWeekDirection(1);
        setWeekStart((ws) => addDays(ws, 7));
        setMobileDayStart(0);
      }
      return;
    }
    setWeekDirection(1);
    setWeekStart((ws) => addDays(ws, 7));
  }, [isMobile, mobileDayStart]);

  const toggleGroupsMenu = useCallback(() => {
    setGroupsOpen((v) => !v);
  }, []);

  const openImportOutlook = useCallback(() => {
    if (readOnly) return;
    setImportOutlookOpen(true);
  }, [readOnly]);

  const closeImportOutlook = useCallback(() => {
    setImportOutlookOpen(false);
  }, []);

  const handleOutlookImported = useCallback(
    async (result) => {
      await reloadWeekEvents();
      const created = result?.created_count ?? result?.created?.length ?? 0;
      if (created > 0) {
        showSuccess(
          t('outlookImport'),
          t('outlookImportAdded', { count: created }),
          4000
        );
      }
    },
    [reloadWeekEvents, showSuccess]
  );

  useEffect(() => {
    if (readOnly) return;
    const params = new URLSearchParams(location.search || '');
    if (params.get('outlook') !== 'connected') return;
    setImportOutlookOpen(true);
    params.delete('outlook');
    const nextSearch = params.toString();
    navigate(
      { pathname: location.pathname, search: nextSearch ? `?${nextSearch}` : '' },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate, readOnly]);

  const getScrollEl = useCallback(() => {
    return scrollRef.current || rootRef.current?.querySelector('[data-week-scroll]');
  }, []);

  const scrollToAm = useCallback(() => {
    setTimePeriod('am');
    requestAnimationFrame(() => {
      getScrollEl()?.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }, [getScrollEl]);

  const scrollToPm = useCallback(() => {
    setTimePeriod('pm');
    requestAnimationFrame(() => {
      const el = getScrollEl();
      if (!el) return;
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    });
  }, [getScrollEl]);

  const setTimePeriodAndScroll = useCallback(
    (period) => {
      if (period === 'pm') scrollToPm();
      else scrollToAm();
    },
    [scrollToAm, scrollToPm]
  );

  // Re-apply AM/PM scroll after week slide remounts the scroll container
  useEffect(() => {
    const id = window.requestAnimationFrame(() => {
      const el = getScrollEl();
      if (!el) return;
      if (timePeriodRef.current === 'pm') {
        el.scrollTop = el.scrollHeight;
      } else {
        el.scrollTop = 0;
      }
    });
    return () => window.cancelAnimationFrame(id);
  }, [weekKey, mobileDayStart, getScrollEl]);

  useImperativeHandle(
    ref,
    () => ({
      goPrevDays,
      goNextDays,
      openCreateEvent,
      toggleGroupsMenu,
      openImportOutlook,
      setSearchQuery,
      scrollToAm,
      scrollToPm,
      setTimePeriod: setTimePeriodAndScroll,
      getWeekRangeLabel: () => weekRangeLabel,
    }),
    [
      goPrevDays,
      goNextDays,
      openCreateEvent,
      toggleGroupsMenu,
      openImportOutlook,
      scrollToAm,
      scrollToPm,
      setTimePeriodAndScroll,
      weekRangeLabel,
    ]
  );

  const applyCreate = useCallback(async () => {
    if (!createState) return;
    const start = timeValueToMinutes(createState.startTime);
    const end = timeValueToMinutes(createState.endTime);
    const endDayIso = createState.endDayIso || createState.dayIso;
    if (!isEndAfterStart(createState.dayIso, start, endDayIso, end, { minutes: true })) {
      setCreateState((s) => (s ? { ...s, timeError: true } : s));
      return;
    }
    const meta = dayMetaByIso(createState.dayIso);
    const tempId = nextId();
    const localEvent = {
      id: tempId,
      day: meta.day,
      date: meta.iso,
      endDate: endDayIso,
      title: createState.title.trim(),
      start,
      end,
      category: createState.category || 'HO',
    };
    setEvents((prev) => [...prev, localEvent]);
    closeCreate();
    try {
      const created = await createEvent(weekEventToApiPayload(localEvent, weekDaysRef.current));
      setEvents((prev) =>
        prev.map((ev) =>
          ev.id === tempId ? apiEventToWeekEvent(created, weekDaysRef.current[0].iso) : ev
        )
      );
    } catch (err) {
      console.error('Failed to create event', err);
      setEvents((prev) => prev.filter((ev) => ev.id !== tempId));
      showEventError(err, t('failedToCreateEvent'));
    }
  }, [createState, closeCreate, dayMetaByIso, showEventError]);

  const applyEdit = useCallback(async () => {
    if (!editState) return;
    const prevEvent = eventsRef.current.find((ev) => ev.id === editState.id);
    if (!prevEvent) {
      closeEdit();
      return;
    }
    const start = timeValueToMinutes(editState.startTime);
    const end = timeValueToMinutes(editState.endTime);
    const dayIso = editState.dayIso || prevEvent.date;
    const endDayIso = editState.endDayIso || dayIso;
    if (!isEndAfterStart(dayIso, start, endDayIso, end, { minutes: true })) {
      setEditState((s) => (s ? { ...s, timeError: true } : s));
      return;
    }
    const meta = dayMetaByIso(dayIso);
    const nextLocal = {
      ...prevEvent,
      day: meta.day,
      date: dayIso,
      endDate: endDayIso,
      start,
      end,
      title: editState.title.trim(),
      category: editState.category,
    };
    setEvents((prev) => prev.map((ev) => (ev.id === editState.id ? nextLocal : ev)));
    closeEdit();
    if (!isPersistedId(editState.id)) return;
    try {
      const updated = await updateEvent(
        editState.id,
        weekEventToApiPayload(nextLocal, weekDaysRef.current)
      );
      setEvents((prev) =>
        prev.map((ev) =>
          ev.id === editState.id
            ? apiEventToWeekEvent(updated, weekDaysRef.current[0].iso)
            : ev
        )
      );
    } catch (err) {
      console.error('Failed to update event', err);
      setEvents((prev) => prev.map((ev) => (ev.id === editState.id ? prevEvent : ev)));
      showEventError(err, t('failedToUpdateEvent'));
    }
  }, [editState, closeEdit, dayMetaByIso, showEventError]);

  const confirmDelete = useCallback(async () => {
    if (!deleteState) return;
    const id = deleteState.id;
    const removed = eventsRef.current.find((ev) => ev.id === id);
    setEvents((prev) => prev.filter((ev) => ev.id !== id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    closeDelete();
    closeEdit();
    if (!removed || !isPersistedId(id)) return;
    try {
      await deleteEvent(id);
    } catch (err) {
      console.error('Failed to delete event', err);
      setEvents((prev) => [...prev, removed]);
      showEventError(err, t('failedToDeleteEvent'));
    }
  }, [deleteState, closeDelete, closeEdit, showEventError]);

  const openEditFor = useCallback((ev, clientRect) => {
    const full = eventsRef.current.find((x) => x.id === ev.id) || ev;
    const startMeta = dayMetaByIso(full.date) || dayMetaByIndex(full.day);
    const endDate = full.endDate || full.date;
    const pos = positionNearRect(clientRect || { left: 100, top: 100, right: 200, bottom: 160, height: 60 }, 320, 420);
    const whenLabel =
      endDate && endDate !== full.date
        ? `${full.date} ${formatMinutes(full.start)} – ${endDate} ${formatMinutes(full.end)}`
        : `${startMeta.label}, ${startMeta.date} · ${formatMinutes(full.start)} – ${formatMinutes(full.end)}`;
    setCreateState(null);
    setDeleteState(null);
    const allDay = isAllDayMinutes(full.start, full.end);
    setEditState({
      id: full.id,
      title: full.title || '',
      category: full.category || 'HO',
      dayIso: full.date,
      endDayIso: endDate,
      startTime: minutesToTimeValue(full.start),
      endTime: minutesToTimeValue(full.end),
      allDay,
      timeError: false,
      whenLabel,
      style: { left: pos.left, top: pos.top },
    });
  }, [dayMetaByIndex, dayMetaByIso]);

  const openDeleteFor = useCallback((ev, clientX, clientY) => {
    const full = eventsRef.current.find((x) => x.id === ev.id) || ev;
    const startMeta = dayMetaByIso(full.date) || dayMetaByIndex(full.day);
    const endDate = full.endDate || full.date;
    const whenLabel =
      endDate && endDate !== full.date
        ? `${full.date} ${formatMinutes(full.start)} – ${endDate} ${formatMinutes(full.end)}`
        : `${startMeta.label}, ${startMeta.date} · ${formatMinutes(full.start)} – ${formatMinutes(full.end)}`;
    setCreateState(null);
    setEditState(null);
    setDeleteState({
      id: full.id,
      title: eventDisplayTitle(full),
      whenLabel,
      cursorX: Number.isFinite(clientX) ? clientX : window.innerWidth / 2,
      cursorY: Number.isFinite(clientY) ? clientY : window.innerHeight / 2,
    });
  }, [dayMetaByIndex, dayMetaByIso]);

  const saveSelectedAsGroup = useCallback(() => {
    const ids = [...selectedRef.current];
    if (ids.length < 2) return;
    const name = groupName.trim() || t('defaultGroupName', { n: groups.length + 1 });
    const selected = eventsRef.current.filter((e) => ids.includes(e.id));
    if (selected.length < 2) return;
    const base = selected.reduce((min, e) => {
      if (e.day < min.day || (e.day === min.day && e.start < min.start)) return e;
      return min;
    }, selected[0]);
    const items = selected.map((e) => ({
      dayOffset: e.day - base.day,
      startOffset: e.start - base.start,
      duration: Math.max(e.end - e.start, 30),
      title: e.title || '',
      category: e.category || 'HO',
    }));
    const next = [
      ...groups,
      { id: `grp-${Date.now()}`, name, items },
    ];
    setGroups(next);
    saveEventGroups(next);
    clearSelection();
  }, [groupName, groups, clearSelection]);

  const applyGroupAt = useCallback(async (group, day, startMinutes) => {
    if (!group?.items?.length) return;
    const weekDays = weekDaysRef.current;
    const created = group.items.map((item) => {
      const dayIndex = Math.max(0, Math.min(6, day + (item.dayOffset || 0)));
      const start = clampMoveStart(startMinutes + (item.startOffset || 0), item.duration || 30);
      const end = clampResizeEnd(start, start + (item.duration || 30));
      const iso = weekDays.find((d) => d.day === dayIndex)?.iso;
      return {
        id: nextId(),
        day: dayIndex,
        date: iso,
        endDate: iso,
        title: item.title || '',
        start,
        end,
        category: item.category || 'HO',
      };
    });
    setEvents((prev) => [...prev, ...created]);
    try {
      const persisted = await Promise.all(
        created.map((ev) => createEvent(weekEventToApiPayload(ev, weekDays)))
      );
      const byTemp = {};
      created.forEach((ev, i) => {
        byTemp[ev.id] = apiEventToWeekEvent(persisted[i], weekDays[0].iso);
      });
      setEvents((prev) => prev.map((ev) => byTemp[ev.id] || ev));
    } catch (err) {
      console.error('Failed to paste group', err);
      try {
        await reloadWeekEvents();
      } catch (_) {
        const tempIds = new Set(created.map((c) => c.id));
        setEvents((prev) => prev.filter((ev) => !tempIds.has(ev.id)));
      }
      showEventError(err, t('failedToPasteGroup'));
    }
  }, [reloadWeekEvents, showEventError]);

  // Global pointer move/up for drag-create / move / copy / resize
  useEffect(() => {
    const onMove = (e) => {
      if (dragCreateRef.current) {
        const st = dragCreateRef.current;
        const y = getColumnY(st.day, e.clientY);
        const current = yToMinutes(y);
        const { start, end } = rangeFromDrag(st.origin, current);
        st.start = start;
        st.end = end;
        setDragSelect({
          day: st.day,
          top: minutesToTop(start),
          height: eventHeight(start, end),
          preview: true,
        });
        return;
      }
      if (moveRef.current) {
        const st = moveRef.current;
        const dx = e.clientX - st.originX;
        const dy = e.clientY - st.originY;
        if (!st.started && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
        if (!st.started) {
          st.started = true;
          setInteraction({ type: 'move', ids: st.members.map((m) => m.id) });
        }
        const day = getDayAt(e.clientX, e.clientY);
        if (day == null) return;

        if (st.mode === 'span') {
          const dayDelta = day - st.originSegmentDay;
          const updates = {};
          st.members.forEach((m) => {
            const nextDate = addDaysToIso(m.sourceDate, dayDelta);
            const nextEndDate = addDaysToIso(m.sourceEndDate, dayDelta);
            const dayIndex = Math.max(0, Math.min(6, m.sourceDay + dayDelta));
            updates[m.id] = {
              day: dayIndex,
              date: nextDate,
              endDate: nextEndDate,
              start: m.sourceStart,
              end: m.sourceEnd,
            };
            m.currentDay = dayIndex;
            m.currentDate = nextDate;
            m.currentEndDate = nextEndDate;
            m.currentStart = m.sourceStart;
            m.currentEnd = m.sourceEnd;
          });
          setEvents((prev) =>
            prev.map((ev) => (updates[ev.id] ? { ...ev, ...updates[ev.id] } : ev))
          );
          return;
        }

        const topY = getColumnY(day, e.clientY) - st.grabOffsetPx;
        const start = clampMoveStart(yToMinutes(topY), st.primaryDuration);
        const updates = {};
        st.members.forEach((m) => {
          const dayIndex = Math.max(0, Math.min(6, day + m.dayOffset));
          const memberStart = clampMoveStart(start + m.startOffset, m.duration);
          const memberEnd = clampResizeEnd(memberStart, memberStart + m.duration);
          const iso = weekDaysRef.current.find((d) => d.day === dayIndex)?.iso;
          updates[m.id] = {
            day: dayIndex,
            date: iso,
            endDate: iso,
            start: memberStart,
            end: memberEnd,
          };
          m.currentDay = dayIndex;
          m.currentStart = memberStart;
          m.currentEnd = memberEnd;
          m.currentDate = iso;
          m.currentEndDate = iso;
        });
        setEvents((prev) =>
          prev.map((ev) => (updates[ev.id] ? { ...ev, ...updates[ev.id] } : ev))
        );
        return;
      }
      if (copyRef.current) {
        const st = copyRef.current;
        const dx = e.clientX - st.originX;
        const dy = e.clientY - st.originY;
        if (!st.started && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
        if (!st.started) {
          st.started = true;
          suppressDeleteRef.current = true;
          const clones = st.members.map((m) => ({
            id: nextId(),
            day: m.sourceDay,
            date: m.sourceDate || weekDaysRef.current.find((d) => d.day === m.sourceDay)?.iso,
            endDate:
              m.sourceEndDate ||
              m.sourceDate ||
              weekDaysRef.current.find((d) => d.day === m.sourceDay)?.iso,
            title: m.title,
            start: m.sourceStart,
            end: m.sourceEnd,
            category: m.category,
            _copyTemp: true,
          }));
          st.cloneIds = clones.map((c) => c.id);
          st.members.forEach((m, i) => {
            m.cloneId = clones[i].id;
          });
          setEvents((prev) => [...prev, ...clones]);
          setInteraction({ type: 'copy', sourceId: st.sourceId, ids: st.cloneIds });
        }
        const day = getDayAt(e.clientX, e.clientY);
        if (day == null) return;

        if (st.mode === 'span') {
          const dayDelta = day - st.originSegmentDay;
          const updates = {};
          st.members.forEach((m) => {
            if (!m.cloneId) return;
            const nextDate = addDaysToIso(m.sourceDate, dayDelta);
            const nextEndDate = addDaysToIso(m.sourceEndDate, dayDelta);
            const dayIndex = Math.max(0, Math.min(6, m.sourceDay + dayDelta));
            updates[m.cloneId] = {
              day: dayIndex,
              date: nextDate,
              endDate: nextEndDate,
              start: m.sourceStart,
              end: m.sourceEnd,
            };
            m.currentDay = dayIndex;
            m.currentDate = nextDate;
            m.currentEndDate = nextEndDate;
            m.currentStart = m.sourceStart;
            m.currentEnd = m.sourceEnd;
          });
          setEvents((prev) =>
            prev.map((ev) => (updates[ev.id] ? { ...ev, ...updates[ev.id] } : ev))
          );
          return;
        }

        const topY = getColumnY(day, e.clientY) - st.grabOffsetPx;
        const start = clampMoveStart(yToMinutes(topY), st.primaryDuration);
        const updates = {};
        st.members.forEach((m) => {
          if (!m.cloneId) return;
          const dayIndex = Math.max(0, Math.min(6, day + m.dayOffset));
          const memberStart = clampMoveStart(start + m.startOffset, m.duration);
          const memberEnd = clampResizeEnd(memberStart, memberStart + m.duration);
          const iso = weekDaysRef.current.find((d) => d.day === dayIndex)?.iso;
          updates[m.cloneId] = {
            day: dayIndex,
            date: iso,
            endDate: iso,
            start: memberStart,
            end: memberEnd,
          };
          m.currentDay = dayIndex;
          m.currentStart = memberStart;
          m.currentEnd = memberEnd;
          m.currentDate = iso;
          m.currentEndDate = iso;
        });
        setEvents((prev) =>
          prev.map((ev) => (updates[ev.id] ? { ...ev, ...updates[ev.id] } : ev))
        );
        return;
      }
      if (resizeRef.current) {
        const st = resizeRef.current;
        const y = getColumnY(st.day, e.clientY);
        const end = clampResizeEnd(st.start, yToEndMinutes(y));
        st.end = end;
        setEvents((prev) =>
          prev.map((ev) => (ev.id === st.id ? { ...ev, end } : ev))
        );
        setInteraction({ type: 'resize', id: st.id });
      }
    };

    const onUp = (e) => {
      if (dragCreateRef.current) {
        const st = dragCreateRef.current;
        dragCreateRef.current = null;
        const col = columnRefs.current[st.day];
        const rect = col
          ? {
              left: col.getBoundingClientRect().left,
              top: minutesToTop(st.start) + col.getBoundingClientRect().top,
              right: col.getBoundingClientRect().right,
              bottom: minutesToTop(st.end) + col.getBoundingClientRect().top,
              height: eventHeight(st.start, st.end),
            }
          : null;
        if (pasteRef.current) {
          applyGroupAt(pasteRef.current, st.day, st.start);
          setDragSelect(null);
          return;
        }
        openCreateAt(st.day, st.start, st.end, rect, false);
        return;
      }
      if (moveRef.current) {
        const st = moveRef.current;
        moveRef.current = null;
        setInteraction(null);
        if (!st.started) {
          // click without drag -> edit or multi-select
          const ev = eventsRef.current.find((x) => x.id === st.primaryId);
          if (!ev) return;
          if (e.ctrlKey || e.metaKey) {
            setSelectedIds((prev) => {
              const next = new Set(prev);
              if (next.has(ev.id)) next.delete(ev.id);
              else next.add(ev.id);
              return next;
            });
          } else if (!suppressClickRef.current) {
            const el = document.querySelector(`[data-event-id="${ev.id}"]`);
            openEditFor(ev, el?.getBoundingClientRect());
          }
        } else {
          suppressClickRef.current = true;
          setTimeout(() => {
            suppressClickRef.current = false;
          }, 0);
          const weekDays = weekDaysRef.current;
          const toPersist = st.members.filter((m) => isPersistedId(m.id));
          if (toPersist.length) {
            (async () => {
              try {
                await Promise.all(
                  toPersist.map((m) => {
                    const ev = eventsRef.current.find((x) => x.id === m.id);
                    const date =
                      m.currentDate ||
                      weekDays.find((d) => d.day === (m.currentDay ?? ev?.day))?.iso ||
                      ev?.date;
                    const endDate = m.currentEndDate || ev?.endDate || date;
                    const payloadEv = {
                      id: m.id,
                      day: m.currentDay ?? ev?.day,
                      date,
                      endDate,
                      title: ev?.title || '',
                      start: m.currentStart ?? ev?.start,
                      end: m.currentEnd ?? ev?.end,
                      category: ev?.category || 'HO',
                    };
                    return updateEvent(m.id, weekEventToApiPayload(payloadEv, weekDays));
                  })
                );
              } catch (err) {
                console.error('Failed to move events', err);
                showEventError(err, t('failedToMoveEvents'));
                try {
                  await reloadWeekEvents();
                } catch (_) {
                  /* ignore */
                }
              }
            })();
          }
        }
        return;
      }
      if (copyRef.current) {
        const st = copyRef.current;
        copyRef.current = null;
        setInteraction(null);
        if (!st.started) {
          // right-click without drag -> delete confirm (handled on contextmenu)
          if (st.cloneIds?.length) {
            setEvents((prev) => prev.filter((ev) => !st.cloneIds.includes(ev.id)));
          }
          return;
        }
        // finalize copies: strip _copyTemp; block delete modal from the trailing contextmenu
        suppressDeleteRef.current = true;
        setTimeout(() => {
          suppressDeleteRef.current = false;
        }, 250);
        const weekDays = weekDaysRef.current;
        const cloneIds = st.cloneIds || [];
        const toCreate = st.members
          .filter((m) => m.cloneId)
          .map((m) => ({
            id: m.cloneId,
            day: m.currentDay ?? m.sourceDay,
            date: m.currentDate || weekDays.find((d) => d.day === (m.currentDay ?? m.sourceDay))?.iso,
            endDate:
              m.currentEndDate ||
              m.currentDate ||
              weekDays.find((d) => d.day === (m.currentDay ?? m.sourceDay))?.iso,
            title: m.title || '',
            start: m.currentStart ?? m.sourceStart,
            end: m.currentEnd ?? m.sourceEnd,
            category: m.category || 'HO',
          }));
        setEvents((prev) =>
          prev.map((ev) => {
            if (cloneIds.includes(ev.id)) {
              const { _copyTemp, ...rest } = ev;
              return rest;
            }
            return ev;
          })
        );
        (async () => {
          try {
            const persisted = await Promise.all(
              toCreate.map((ev) => createEvent(weekEventToApiPayload(ev, weekDays)))
            );
            const byTemp = {};
            toCreate.forEach((ev, i) => {
              byTemp[ev.id] = apiEventToWeekEvent(persisted[i], weekDays[0].iso);
            });
            setEvents((prev) => prev.map((ev) => byTemp[ev.id] || ev));
          } catch (err) {
            console.error('Failed to copy events', err);
            try {
              await reloadWeekEvents();
            } catch (_) {
              setEvents((prev) => prev.filter((ev) => !cloneIds.includes(ev.id)));
            }
            showEventError(err, t('failedToCopyEvents'));
          }
        })();
        suppressClickRef.current = true;
        setTimeout(() => {
          suppressClickRef.current = false;
        }, 0);
        return;
      }
      if (resizeRef.current) {
        const st = resizeRef.current;
        resizeRef.current = null;
        setInteraction(null);
        if (isPersistedId(st.id)) {
          const weekDays = weekDaysRef.current;
          const ev = eventsRef.current.find((x) => x.id === st.id);
          const payloadEv = {
            id: st.id,
            day: st.day,
            date: weekDays.find((d) => d.day === st.day)?.iso || ev?.date,
            endDate: weekDays.find((d) => d.day === st.day)?.iso || ev?.endDate || ev?.date,
            title: ev?.title || '',
            start: st.start,
            end: st.end,
            category: ev?.category || 'HO',
          };
          (async () => {
            try {
              await updateEvent(st.id, weekEventToApiPayload(payloadEv, weekDays));
            } catch (err) {
              console.error('Failed to resize event', err);
              setEvents((prev) =>
                prev.map((x) => (x.id === st.id ? { ...x, end: st.originalEnd } : x))
              );
              showEventError(err, t('failedToResizeEvent'));
            }
          })();
        }
      }
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [getColumnY, getDayAt, openCreateAt, openEditFor, applyGroupAt]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (pasteGroup) {
          exitPaste();
          return;
        }
        if (groupsOpen) {
          setGroupsOpen(false);
          return;
        }
        if (createState) {
          closeCreate();
          return;
        }
        if (editState) {
          closeEdit();
          return;
        }
        if (deleteState) {
          closeDelete();
          return;
        }
        if (selectedIds.size) {
          clearSelection();
          return;
        }
        if (copyRef.current?.cloneIds) {
          const ids = copyRef.current.cloneIds;
          copyRef.current = null;
          setInteraction(null);
          setEvents((prev) => prev.filter((ev) => !ids.includes(ev.id)));
        }
      }
      if (readOnly) return;
      if (e.key === 'N' && e.shiftKey && !e.ctrlKey && !e.metaKey) {
        const tag = e.target?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA') return;
        e.preventDefault();
        openCreateEvent();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [
    pasteGroup,
    groupsOpen,
    createState,
    editState,
    deleteState,
    selectedIds,
    exitPaste,
    closeCreate,
    closeEdit,
    closeDelete,
    clearSelection,
    openCreateEvent,
    readOnly,
  ]);

  const onColumnPointerDown = useCallback(
    (e, day) => {
      if (readOnly) return;
      if (e.button !== 0) return;
      if (e.target.closest('.calendar-event')) return;
      if (e.target.closest('.current-time-line')) return;
      e.preventDefault();
      const y = getColumnY(day, e.clientY);
      const origin = yToMinutes(y);
      if (pasteRef.current) {
        applyGroupAt(pasteRef.current, day, origin);
        return;
      }
      closeEdit();
      closeDelete();
      setGroupsOpen(false);
      dragCreateRef.current = { day, origin, start: origin, end: origin + 30 };
      setDragSelect({
        day,
        top: minutesToTop(origin),
        height: eventHeight(origin, origin + 30),
        preview: true,
      });
    },
    [getColumnY, applyGroupAt, closeEdit, closeDelete, readOnly]
  );

  const onColumnPointerMove = useCallback(
    (e, day) => {
      if (readOnly) return;
      if (dragCreateRef.current || moveRef.current || copyRef.current || resizeRef.current) return;
      const y = getColumnY(day, e.clientY);
      const mins = yToMinutes(y);
      setSlotHover({ day, top: minutesToTop(mins) });
    },
    [getColumnY, readOnly]
  );

  const onColumnPointerLeave = useCallback((day) => {
    setSlotHover((prev) => (prev?.day === day ? null : prev));
  }, []);

  const onEventPointerDown = useCallback(
    (e, ev) => {
      if (readOnly) return;
      const full = eventsRef.current.find((x) => x.id === ev.id) || ev;
      const isMulti = Boolean(full.endDate && full.endDate !== full.date);

      if (e.button === 2) {
        // right button — copy drag
        e.preventDefault();
        e.stopPropagation();
        const col = columnRefs.current[ev.day];
        const grabOffsetPx = col
          ? e.clientY - col.getBoundingClientRect().top - minutesToTop(ev.start)
          : 0;
        const selected = selectedRef.current;
        const moving =
          selected.has(full.id) && selected.size >= 2
            ? eventsRef.current.filter((x) => selected.has(x.id))
            : [full];
        const primary = full;
        copyRef.current = {
          sourceId: full.id,
          mode: isMulti ? 'span' : 'timed',
          originX: e.clientX,
          originY: e.clientY,
          originSegmentDay: ev.day,
          started: false,
          grabOffsetPx,
          primaryDuration: Math.max(
            isMulti ? 60 : full.end - full.start,
            30
          ),
          members: moving.map((m) => ({
            id: m.id,
            sourceDay: m.day,
            sourceDate: m.date,
            sourceEndDate: m.endDate || m.date,
            sourceStart: m.start,
            sourceEnd: m.end,
            title: m.title,
            category: m.category,
            dayOffset: m.day - primary.day,
            startOffset: m.start - primary.start,
            duration: Math.max(m.end - m.start, 30),
            cloneId: null,
          })),
        };
        return;
      }
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      closeCreate();
      setGroupsOpen(false);
      const col = columnRefs.current[ev.day];
      const grabOffsetPx = col
        ? e.clientY - col.getBoundingClientRect().top - minutesToTop(ev.start)
        : 0;
      const selected = selectedRef.current;
      const moving =
        selected.has(full.id) && selected.size >= 2
          ? eventsRef.current.filter((x) => selected.has(x.id))
          : [full];
      const primary = full;
      moveRef.current = {
        mode: isMulti ? 'span' : 'timed',
        primaryId: full.id,
        originX: e.clientX,
        originY: e.clientY,
        originSegmentDay: ev.day,
        started: false,
        grabOffsetPx,
        primaryDuration: Math.max(isMulti ? 60 : full.end - full.start, 30),
        members: moving.map((m) => ({
          id: m.id,
          sourceDay: m.day,
          sourceDate: m.date,
          sourceEndDate: m.endDate || m.date,
          sourceStart: m.start,
          sourceEnd: m.end,
          dayOffset: m.day - primary.day,
          startOffset: m.start - primary.start,
          duration: Math.max(m.end - m.start, 30),
        })),
      };
    },
    [closeCreate, readOnly]
  );

  const onEventContextMenu = useCallback(
    (e, ev) => {
      if (readOnly) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      // Right-drag copy should not open the delete modal
      if (suppressDeleteRef.current || (copyRef.current && copyRef.current.started)) {
        return;
      }
      if (copyRef.current && !copyRef.current.started) {
        copyRef.current = null;
      }
      openDeleteFor(ev, e.clientX, e.clientY);
    },
    [openDeleteFor, readOnly]
  );

  const onResizePointerDown = useCallback((e, ev) => {
    if (readOnly) return;
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = {
      id: ev.id,
      day: ev.day,
      start: ev.start,
      end: ev.end,
      originalEnd: ev.end,
    };
    setInteraction({ type: 'resize', id: ev.id });
  }, [readOnly]);

  const calendarSlideKey = isMobile
    ? `${weekKey}-${mobileDayStart}`
    : String(weekKey);

  return (
    <div
      ref={rootRef}
      className={`weekly-view-root${pasteGroup ? ' is-pasting-group' : ''}${readOnly ? ' is-readonly' : ''}`}
    >
      {eventsLoading && (
        <div className="absolute inset-x-0 top-0 z-20 px-4 py-2 text-xs text-text-secondary bg-surface/90 border-b border-border">
          Loading events…
        </div>
      )}
      <Toast
        show={toast.show}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={hideToast}
        duration={toast.duration}
      />
      <div className="flex-1 min-h-0 relative overflow-hidden flex flex-col">
        <AnimatePresence initial={false} mode="wait" custom={weekDirection}>
          <motion.div
            key={calendarSlideKey}
            custom={weekDirection}
            variants={weekSlideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.28, ease: [0.4, 0, 0.2, 1] }}
            className="absolute inset-0 flex flex-col min-h-0 bg-background"
          >
            <WeekDayHeaders
              weekDays={weekDays}
              isMobile={isMobile}
              visibleDays={visibleDays}
            />
            <div
              ref={scrollRef}
              data-week-scroll
              className="flex-1 overflow-y-auto scrollbar-hide relative bg-surface min-h-0"
            >
              <div className="flex min-h-full">
                <TimeGutter />
                <div className="week-day-grid flex-1 grid grid-cols-7 grid-bg relative">
                  <div className="week-bg-grid absolute inset-0 grid grid-cols-7 pointer-events-none">
                    {weekDays.map((d) => (
                      <div
                        key={d.day}
                        className={`week-bg-cell border-r border-border/40 h-full last:border-r-0${
                          isMobile && !visibleDays.has(d.day) ? ' is-mobile-hidden' : ''
                        }`}
                        data-day={d.day}
                      />
                    ))}
                  </div>
                  {weekDays.map((d) => (
                    <DayColumn
                      key={`${weekKey}-${d.day}`}
                      dayMeta={d}
                      events={eventsByDay[d.day] || []}
                      layouts={layouts}
                      selectedIds={selectedIds}
                      interaction={interaction}
                      nowMinutes={nowMinutes}
                      isMobileHidden={isMobile && !visibleDays.has(d.day)}
                      columnRefs={columnRefs}
                      slotHover={slotHover}
                      dragSelect={dragSelect}
                      onColumnPointerDown={onColumnPointerDown}
                      onColumnPointerMove={onColumnPointerMove}
                      onColumnPointerLeave={onColumnPointerLeave}
                      onEventPointerDown={onEventPointerDown}
                      onEventContextMenu={onEventContextMenu}
                      onResizePointerDown={onResizePointerDown}
                    />
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className={`${readOnly ? 'absolute' : 'fixed'} bottom-6 right-6 ${readOnly ? 'z-20' : 'z-50'}`}>
        <div
          className="flex gap-1 bg-surface p-1 rounded-lg border border-border shadow-[0_4px_12px_rgba(15,23,42,0.12)]"
          role="group"
          aria-label={t('amOrPm')}
        >
          <button
            type="button"
            className={
              timePeriod === 'am'
                ? 'px-3 py-1.5 text-xs font-bold bg-primary text-white rounded-md shadow-sm'
                : 'px-3 py-1.5 text-xs font-medium text-text-secondary hover:text-text-main rounded-md transition-colors'
            }
            aria-pressed={timePeriod === 'am'}
            onClick={scrollToAm}
          >
            AM
          </button>
          <button
            type="button"
            className={
              timePeriod === 'pm'
                ? 'px-3 py-1.5 text-xs font-bold bg-primary text-white rounded-md shadow-sm'
                : 'px-3 py-1.5 text-xs font-medium text-text-secondary hover:text-text-main rounded-md transition-colors'
            }
            aria-pressed={timePeriod === 'pm'}
            onClick={scrollToPm}
          >
            PM
          </button>
        </div>
      </div>

      {!readOnly && (
        <>
          <SelectionBar
            count={selectedIds.size}
            groupName={groupName}
            onGroupNameChange={setGroupName}
            onSave={saveSelectedAsGroup}
            onClear={clearSelection}
          />
          <PasteGroupBanner
            label={
              pasteGroup
                ? t('pasteGroupHint', { name: pasteGroup.name })
                : null
            }
            onCancel={exitPaste}
          />
          <GroupsDropdown
            open={groupsOpen}
            groups={groups}
            onClose={() => setGroupsOpen(false)}
            onApply={(group) => {
              setGroupsOpen(false);
              clearSelection();
              closeCreate();
              closeEdit();
              setPasteGroup(group);
            }}
            onDelete={(id) => {
              const next = groups.filter((g) => g.id !== id);
              setGroups(next);
              saveEventGroups(next);
              if (pasteGroup?.id === id) exitPaste();
            }}
          />

          <CreateEventPopover
            open={!!createState}
            style={createState?.style}
            heading={createState?.heading}
            dayIso={createState?.dayIso || ''}
            endDayIso={createState?.endDayIso || createState?.dayIso || ''}
            startTime={createState?.startTime || '09:00'}
            endTime={createState?.endTime || '10:00'}
            allDay={!!createState?.allDay}
            title={createState?.title || ''}
            category={createState?.category || 'HO'}
            timeError={!!createState?.timeError}
            onChange={(patch) => {
              setCreateState((s) => {
                if (!s) return s;
                if (Object.prototype.hasOwnProperty.call(patch, 'allDay')) {
                  const toggled = applyAllDayToggle(s, !!patch.allDay);
                  const meta = dayMetaByIso(toggled.dayIso);
                  const endIso = toggled.endDayIso || toggled.dayIso;
                  if (endIso !== toggled.dayIso) {
                    toggled.heading = `${toggled.dayIso} ${formatMinutes(toggled.start)} – ${endIso} ${formatMinutes(toggled.end)}`;
                  } else {
                    toggled.heading = `${meta.label}, ${meta.date} · ${formatMinutes(toggled.start)} – ${formatMinutes(toggled.end)}`;
                  }
                  return toggled;
                }
                const next = { ...s, ...patch, timeError: false };
                if (patch.dayIso && (!s.endDayIso || s.endDayIso < patch.dayIso)) {
                  next.endDayIso = patch.dayIso;
                }
                if (patch.startTime || patch.endTime || patch.dayIso || patch.endDayIso) {
                  const meta = dayMetaByIso(next.dayIso);
                  const start = timeValueToMinutes(next.startTime);
                  const end = timeValueToMinutes(next.endTime);
                  const endIso = next.endDayIso || next.dayIso;
                  if (endIso !== next.dayIso) {
                    next.heading = `${next.dayIso} ${formatMinutes(start)} – ${endIso} ${formatMinutes(end)}`;
                  } else {
                    next.heading = `${meta.label}, ${meta.date} · ${formatMinutes(start)} – ${formatMinutes(end)}`;
                  }
                  next.day = meta.day;
                  next.start = start;
                  next.end = end;
                  next.allDay = isAllDayTimes(next.startTime, next.endTime);
                }
                return next;
              });

              if (Object.prototype.hasOwnProperty.call(patch, 'allDay')) {
                if (patch.allDay) {
                  setDragSelect(null);
                }
                return;
              }

              if (
                !(patch.startTime || patch.endTime || patch.dayIso || patch.endDayIso) ||
                !createState
              ) {
                return;
              }
              const dayIso = patch.dayIso ?? createState.dayIso;
              const endDayIso = patch.endDayIso ?? createState.endDayIso ?? dayIso;
              const startTime = patch.startTime ?? createState.startTime;
              const endTime = patch.endTime ?? createState.endTime;
              const meta = dayMetaByIso(dayIso);
              const start = timeValueToMinutes(startTime);
              const end = timeValueToMinutes(endTime);
              if (endDayIso === dayIso && end > start) {
                setDragSelect({
                  day: meta.day,
                  top: minutesToTop(start),
                  height: eventHeight(start, end),
                  preview: false,
                });
              } else {
                setDragSelect(null);
              }
            }}
            onClose={closeCreate}
            onSubmit={applyCreate}
          />

          <EditEventPopover
            open={!!editState}
            style={editState?.style}
            whenLabel={editState?.whenLabel}
            dayIso={editState?.dayIso || ''}
            endDayIso={editState?.endDayIso || editState?.dayIso || ''}
            startTime={editState?.startTime || '09:00'}
            endTime={editState?.endTime || '10:00'}
            allDay={!!editState?.allDay}
            title={editState?.title || ''}
            category={editState?.category || 'HO'}
            timeError={!!editState?.timeError}
            onChange={(patch) =>
              setEditState((s) => {
                if (!s) return s;
                if (Object.prototype.hasOwnProperty.call(patch, 'allDay')) {
                  return applyAllDayToggle(s, !!patch.allDay);
                }
                const next = { ...s, ...patch, timeError: false };
                if (patch.dayIso && (!next.endDayIso || next.endDayIso < patch.dayIso)) {
                  next.endDayIso = patch.dayIso;
                }
                if (patch.startTime || patch.endTime) {
                  next.allDay = isAllDayTimes(next.startTime, next.endTime);
                }
                return next;
              })
            }
            onClose={closeEdit}
            onSubmit={applyEdit}
            onDelete={() => {
              const ev = events.find((x) => x.id === editState?.id);
              if (!ev) return;
              const cx = typeof editState.style?.left === 'number'
                ? editState.style.left + 160
                : window.innerWidth / 2;
              const cy = typeof editState.style?.top === 'number'
                ? editState.style.top + 200
                : window.innerHeight / 2;
              openDeleteFor(ev, cx, cy);
            }}
          />

          <DeleteConfirmModal
            open={!!deleteState}
            cursorX={deleteState?.cursorX}
            cursorY={deleteState?.cursorY}
            title={deleteState?.title}
            whenLabel={deleteState?.whenLabel}
            onCancel={closeDelete}
            onConfirm={confirmDelete}
          />

          <ImportOutlookModal
            isOpen={importOutlookOpen}
            onClose={closeImportOutlook}
            weekStartIso={weekDays[0]?.iso}
            returnToPath={
              weekDays[0]?.iso
                ? `/employee-dashboard/planning/week/${weekDays[0].iso}`
                : '/employee-dashboard/planning/week'
            }
            onImported={handleOutlookImported}
          />
        </>
      )}
    </div>
  );
});

export default EmployeeWeeklyViewPage;
